import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Modal } from "react-bootstrap";
import swal from "sweetalert";
import PageTitle from "../../layouts/PageTitle";
import {
  fetchRestaurantAdmins,
  toggleRestaurantAdminActive,
  archiveRestaurantAdmin,
  updateRestaurantAdminPermissions,
} from "../../../services/RestaurantAdminsService";
import { formatPhoneDisplay } from "../../../utils/formatPhoneDisplay";
import "./RestaurantUsers.css";

const PAGE_SIZE = 10;

const AVAILABLE_MODULES = [
  {
    id: "home",
    icon: "fas fa-home",
    nameKey: "moduleHomeName",
    defaultName: "Home & Dashboard",
    descKey: "moduleHomeDesc",
    defaultDesc:
      "Main operational dashboard, key performance metrics and overview.",
  },
  {
    id: "restaurant-info",
    icon: "fas fa-store",
    nameKey: "moduleRestaurantInfoName",
    defaultName: "Restaurant Information",
    descKey: "moduleRestaurantInfoDesc",
    defaultDesc:
      "Restaurant profile, opening hours, addresses, settings & contacts.",
  },
  {
    id: "pos",
    icon: "fas fa-cash-register",
    nameKey: "modulePosName",
    defaultName: "Point of Sale & Orders",
    descKey: "modulePosDesc",
    defaultDesc: "Live orders, kitchen board, and table reservations.",
  },
  {
    id: "kitchen",
    icon: "fas fa-utensils",
    nameKey: "moduleKitchenName",
    defaultName: "Kitchen & Menu",
    descKey: "moduleKitchenDesc",
    defaultDesc: "Menu list, categories, subcategories, products & choices.",
  },
  {
    id: "clients",
    icon: "fas fa-user-friends",
    nameKey: "moduleClientsName",
    defaultName: "Clients & Guests",
    descKey: "moduleClientsDesc",
    defaultDesc: "Customer directory, visit history & contact details.",
  },
  {
    id: "events",
    icon: "fas fa-bullhorn",
    nameKey: "moduleEventsName",
    defaultName: "Events & Promotions",
    descKey: "moduleEventsDesc",
    defaultDesc: "Special events, event bookings & promotional banners.",
  },
  {
    id: "support",
    icon: "fas fa-headset",
    nameKey: "moduleSupportName",
    defaultName: "Customer Support & Claims",
    descKey: "moduleSupportDesc",
    defaultDesc: "Support requests, reclamations, feedback & lost-found.",
  },
  {
    id: "qrcode",
    icon: "fas fa-qrcode",
    nameKey: "moduleQrName",
    defaultName: "QR Code Generator",
    descKey: "moduleQrDesc",
    defaultDesc: "Table QR codes creation and print exports.",
  },
  {
    id: "admin-history",
    icon: "fas fa-history",
    nameKey: "moduleHistoryName",
    defaultName: "Admin Action History",
    descKey: "moduleHistoryDesc",
    defaultDesc: "Backoffice audit logs and administrator activity history.",
  },
];

// Safe translation helper: returns fallback if key is missing or equal to key string
const tr = (t, key, fallback) => {
  if (!t || typeof t !== "function") return fallback;
  const res = t(key);
  return !res || res === key ? fallback : res;
};

const getInitials = (firstName = "", lastName = "") => {
  const f = String(firstName || "").trim().charAt(0);
  const l = String(lastName || "").trim().charAt(0);
  const initials = `${f}${l}`.toUpperCase();
  return initials || "?";
};

const RestaurantUsers = () => {
  const { t } = useTranslation();
  const [admins, setAdmins] = useState([]);
  const [restaurantName, setRestaurantName] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [selectedUserModal, setSelectedUserModal] = useState(null);
  const [isEditingPerms, setIsEditingPerms] = useState(false);
  const [editPermsSelection, setEditPermsSelection] = useState([]);
  const [savingPerms, setSavingPerms] = useState(false);

  const handleOpenUserModal = (admin, editMode = false) => {
    setSelectedUserModal(admin);
    setIsEditingPerms(editMode && !admin.isOwner);
    const perms =
      admin.permissions ||
      admin.modules ||
      admin.accessPermissions ||
      AVAILABLE_MODULES.map((m) => m.id);
    setEditPermsSelection(
      Array.isArray(perms) ? [...perms] : AVAILABLE_MODULES.map((m) => m.id)
    );
  };

  const toggleEditPerm = (id) => {
    setEditPermsSelection((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const selectAllEditPerms = () => {
    setEditPermsSelection(AVAILABLE_MODULES.map((m) => m.id));
  };

  const clearAllEditPerms = () => {
    setEditPermsSelection([]);
  };

  const handleSavePermissions = async () => {
    if (!selectedUserModal?._id) return;
    if (editPermsSelection.length === 0) {
      swal(
        tr(t, "warning", "Warning"),
        tr(
          t,
          "restaurantUsersSelectPermission",
          "Please select at least one module access permission for this user."
        ),
        "warning"
      );
      return;
    }

    setSavingPerms(true);
    try {
      await updateRestaurantAdminPermissions(
        selectedUserModal._id,
        editPermsSelection
      );
      setSelectedUserModal((prev) => ({
        ...prev,
        permissions: editPermsSelection,
      }));
      setAdmins((prev) =>
        prev.map((a) =>
          a._id === selectedUserModal._id
            ? { ...a, permissions: editPermsSelection }
            : a
        )
      );
      setIsEditingPerms(false);
      swal(
        tr(t, "success", "Success"),
        tr(
          t,
          "restaurantUsersPermissionsUpdated",
          "Permissions updated successfully."
        ),
        "success"
      );
    } catch (error) {
      swal(
        tr(t, "error", "Error"),
        error.response?.data?.message ||
          tr(
            t,
            "restaurantUsersPermissionsUpdateError",
            "Failed to update permissions."
          ),
        "error"
      );
    } finally {
      setSavingPerms(false);
    }
  };

  // Filters state
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  const loadAdmins = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchRestaurantAdmins();
      const archives = JSON.parse(
        localStorage.getItem("archived_restaurant_admins") || "[]"
      );
      const deleted = JSON.parse(
        localStorage.getItem("deleted_restaurant_admins") || "[]"
      );
      const rawAdmins = Array.isArray(data?.admins) ? data.admins : [];
      const activeAdmins = rawAdmins.filter(
        (a) =>
          !a.isArchived &&
          !archives.includes(a._id) &&
          !deleted.includes(a._id)
      );
      setAdmins(activeAdmins);
      setRestaurantName(data?.restaurantName || "");
    } catch (error) {
      console.error("Error fetching restaurant admins:", error);
      setAdmins([]);
      swal(
        tr(t, "restaurantUsersLoadErrorTitle", "Error"),
        error.response?.data?.message ||
          tr(t, "restaurantUsersLoadError", "Failed to load users."),
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadAdmins();
  }, [loadAdmins]);

  const handleToggleActive = useCallback(
    async (admin) => {
      if (!admin?._id) return;

      const actionLabel = admin.activate
        ? tr(t, "restaurantUsersDeactivate", "deactivate")
        : tr(t, "restaurantUsersActivate", "activate");

      const confirmed = await swal({
        title: tr(t, "restaurantUsersToggleConfirmTitle", "Are you sure?"),
        text:
          t("restaurantUsersToggleConfirmText", {
            action: actionLabel,
            name: `${admin.firstName || ""} ${admin.lastName || ""}`.trim(),
          }) ||
          `Do you want to ${actionLabel} this admin?`,
        icon: "warning",
        buttons: true,
      });

      if (!confirmed) return;

      setBusyId(admin._id);
      try {
        await toggleRestaurantAdminActive(admin._id);
        await loadAdmins();
        swal(
          tr(t, "success", "Success"),
          tr(t, "restaurantUsersToggleSuccess", "Admin status updated."),
          "success"
        );
      } catch (error) {
        swal(
          tr(t, "restaurantUsersLoadErrorTitle", "Error"),
          error.response?.data?.message ||
            tr(t, "restaurantUsersToggleError", "Failed to update admin status."),
          "error"
        );
      } finally {
        setBusyId("");
      }
    },
    [loadAdmins, t]
  );

  const handleArchive = useCallback(
    async (admin) => {
      if (!admin?._id) return;
      const adminName = `${admin.firstName || ""} ${admin.lastName || ""}`.trim();
      const confirmed = await swal({
        title: tr(t, "restaurantUsersArchiveConfirmTitle", "Archive User?"),
        text:
          t("restaurantUsersArchiveConfirmText", { name: adminName }) ||
          `Are you sure you want to archive ${adminName}? The user will be moved to Archived Users.`,
        icon: "warning",
        buttons: [tr(t, "cancel", "Cancel"), tr(t, "archive", "Archive")],
        dangerMode: true,
      });

      if (!confirmed) return;

      setBusyId(admin._id);
      try {
        await archiveRestaurantAdmin(admin._id);
        await loadAdmins();
        swal(
          tr(t, "success", "Success"),
          tr(t, "restaurantUsersArchiveSuccess", "User has been moved to Archives."),
          "success"
        );
      } catch (error) {
        swal(
          tr(t, "error", "Error"),
          error.response?.data?.message ||
            tr(t, "restaurantUsersArchiveError", "Failed to archive user."),
          "error"
        );
      } finally {
        setBusyId("");
      }
    },
    [loadAdmins, t]
  );

  // Filtered & Paginated list
  const filteredAdmins = useMemo(() => {
    return admins.filter((admin) => {
      // Search
      const query = searchQuery.trim().toLowerCase();
      if (query) {
        const fullName = `${admin.firstName || ""} ${admin.lastName || ""}`.toLowerCase();
        const email = String(admin.email || "").toLowerCase();
        const phone = String(admin.phone || "").toLowerCase();
        if (!fullName.includes(query) && !email.includes(query) && !phone.includes(query)) {
          return false;
        }
      }
      // Role
      if (roleFilter === "owner" && !admin.isOwner) return false;
      if (roleFilter === "admin" && admin.isOwner) return false;
      // Status
      if (statusFilter === "active" && !admin.activate) return false;
      if (statusFilter === "inactive" && admin.activate) return false;

      return true;
    });
  }, [admins, searchQuery, roleFilter, statusFilter]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, roleFilter, statusFilter]);

  // Metrics
  const stats = useMemo(() => {
    const total = admins.length;
    const active = admins.filter((a) => a.activate).length;
    const inactive = admins.filter((a) => !a.activate).length;
    const owners = admins.filter((a) => a.isOwner).length;
    return { total, active, inactive, owners };
  }, [admins]);

  const totalPages = Math.max(1, Math.ceil(filteredAdmins.length / PAGE_SIZE));
  const activePage = Math.min(currentPage, totalPages);
  const paginatedAdmins = useMemo(() => {
    const start = (activePage - 1) * PAGE_SIZE;
    return filteredAdmins.slice(start, start + PAGE_SIZE);
  }, [filteredAdmins, activePage]);

  const hasActiveFilters = Boolean(searchQuery || roleFilter !== "all" || statusFilter !== "all");

  const clearFilters = () => {
    setSearchQuery("");
    setRoleFilter("all");
    setStatusFilter("all");
  };

  return (
    <>
      <PageTitle
        activeMenu={tr(t, "restaurantUsersTitle", "Users")}
        motherMenu={tr(t, "navGroupRestaurant", "Restaurant")}
      />

      <div className="ru-page">
        {/* Hero Header */}
        <header className="ru-hero">
          <div className="ru-hero__copy">
            <div className="ru-hero__icon-wrap">
              <i className="fas fa-user-shield" aria-hidden="true" />
            </div>
            <div className="ru-hero__text">
              <span className="ru-hero__eyebrow">
                <i className="fas fa-crown" aria-hidden="true" /> Backoffice
              </span>
              <h2 className="ru-hero__title">
                {tr(t, "restaurantUsersTitle", "Users")}
              </h2>
              {restaurantName ? (
                <p className="ru-hero__subtitle">{restaurantName}</p>
              ) : (
                <p className="ru-hero__subtitle">
                  {tr(t, "restaurantUsersSubtitle", "Manage restaurant team members and permissions")}
                </p>
              )}
            </div>
          </div>
          <div className="ru-hero__actions">
            <Link to="/add-restaurant-user" className="ru-btn ru-btn-primary">
              <i className="fas fa-user-plus" aria-hidden="true" />
              <span>{tr(t, "restaurantUsersAddButton", "Add User")}</span>
            </Link>
          </div>
        </header>

        {/* Metric Cards */}
        <div className="ru-stats">
          <div className="ru-stat-card">
            <div className="ru-stat-icon ru-stat-icon--primary">
              <i className="fas fa-users" aria-hidden="true" />
            </div>
            <div className="ru-stat-info">
              <span className="ru-stat-value">{stats.total}</span>
              <span className="ru-stat-label">{tr(t, "restaurantUsersTotal", "Total")}</span>
            </div>
          </div>
          <div className="ru-stat-card">
            <div className="ru-stat-icon ru-stat-icon--success">
              <i className="fas fa-user-check" aria-hidden="true" />
            </div>
            <div className="ru-stat-info">
              <span className="ru-stat-value">{stats.active}</span>
              <span className="ru-stat-label">{tr(t, "restaurantUsersActive", "Active")}</span>
            </div>
          </div>
          <div className="ru-stat-card">
            <div className="ru-stat-icon ru-stat-icon--danger">
              <i className="fas fa-user-slash" aria-hidden="true" />
            </div>
            <div className="ru-stat-info">
              <span className="ru-stat-value">{stats.inactive}</span>
              <span className="ru-stat-label">{tr(t, "restaurantUsersInactive", "Inactive")}</span>
            </div>
          </div>
          <div className="ru-stat-card">
            <div className="ru-stat-icon ru-stat-icon--warning">
              <i className="fas fa-crown" aria-hidden="true" />
            </div>
            <div className="ru-stat-info">
              <span className="ru-stat-value">{stats.owners}</span>
              <span className="ru-stat-label">{tr(t, "restaurantUsersOwners", "Owners")}</span>
            </div>
          </div>
        </div>

        {/* Toolbar & Main Table Panel */}
        <div className="ru-toolbar-panel">
          {/* Controls Bar */}
          <div className="ru-toolbar">
            <div className="ru-toolbar__left">
              <div className="ru-search-box">
                <i className="fas fa-search" aria-hidden="true" />
                <input
                  type="text"
                  className="ru-search-input"
                  placeholder={tr(t, "restaurantUsersSearchPlaceholder", "Search...")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <select
                className="ru-filter-select"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <option value="all">{tr(t, "restaurantUsersRoleAll", "Role")}</option>
                <option value="owner">{tr(t, "restaurantUsersPrimaryOwner", "Primary Owner")}</option>
                <option value="admin">{tr(t, "restaurantUsersAdmin", "Admin")}</option>
              </select>

              <select
                className="ru-filter-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">{tr(t, "restaurantUsersStatusAll", "Status")}</option>
                <option value="active">{tr(t, "restaurantUsersActive", "Active")}</option>
                <option value="inactive">{tr(t, "restaurantUsersInactive", "Inactive")}</option>
              </select>

              {hasActiveFilters && (
                <button
                  type="button"
                  className="ru-clear-filter-btn"
                  onClick={clearFilters}
                >
                  <i className="fas fa-times me-1" aria-hidden="true" />
                  {tr(t, "clearFilters", "Clear Filters")}
                </button>
              )}
            </div>

            <div className="ru-toolbar__right">
              <span className="ru-count-badge">
                {filteredAdmins.length} users
              </span>
            </div>
          </div>

          {/* Table Area */}
          <div className="ru-table-wrap">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">{tr(t, "loading", "Loading...")}</span>
                </div>
                <p className="text-muted mt-2 mb-0">{tr(t, "loading", "Loading...")}</p>
              </div>
            ) : paginatedAdmins.length === 0 ? (
              <div className="ru-empty-state">
                <div className="ru-empty-icon">
                  <i className="fas fa-user-slash" aria-hidden="true" />
                </div>
                <h4 className="ru-empty-title">{tr(t, "restaurantUsersEmpty", "No users found")}</h4>
                <p className="ru-empty-desc">
                  {hasActiveFilters
                    ? tr(t, "restaurantUsersEmptyFilters", "Try adjusting your search query or filters.")
                    : tr(t, "restaurantUsersEmptyHint", "Click 'Add User' to invite new administrators.")}
                </p>
              </div>
            ) : (
              <table className="ru-table">
                <thead>
                  <tr>
                    <th>{tr(t, "restaurantUsersColUser", "User")}</th>
                    <th>{tr(t, "restaurantUsersColPhone", "Phone")}</th>
                    <th>{tr(t, "restaurantUsersColRole", "Role")}</th>
                    <th>{tr(t, "restaurantUsersColStatus", "Status")}</th>
                    <th className="text-end">{tr(t, "restaurantUsersColActions", "Actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedAdmins.map((admin) => {
                    const fullName = `${admin.firstName || ""} ${admin.lastName || ""}`.trim() || "—";
                    const initials = getInitials(admin.firstName, admin.lastName);
                    const isBusy = busyId === admin._id;

                    return (
                      <tr key={admin._id || admin.email}>
                        <td>
                          <div className="ru-user-identity">
                            <div className={`ru-avatar ${admin.isOwner ? "ru-avatar--owner" : ""}`}>
                              {initials}
                            </div>
                            <div className="ru-user-name-group">
                              <span className="ru-user-name">{fullName}</span>
                              <span className="ru-user-email">{admin.email || "—"}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          {admin.phone ? (
                            <a
                              href={`tel:${formatPhoneDisplay(admin.phone)}`}
                              className="ru-contact-link"
                            >
                              <i className="fas fa-phone-alt" aria-hidden="true" />
                              <span>{formatPhoneDisplay(admin.phone)}</span>
                            </a>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          {admin.isOwner ? (
                            <span className="ru-badge ru-badge--owner">
                              <i className="fas fa-crown me-1" aria-hidden="true" />
                              {tr(t, "restaurantUsersPrimaryOwner", "Primary Owner")}
                            </span>
                          ) : (
                            <span className="ru-badge ru-badge--admin">
                              <i className="fas fa-shield-alt me-1" aria-hidden="true" />
                              {tr(t, "restaurantUsersAdmin", "Admin")}
                            </span>
                          )}
                        </td>
                        <td>
                          {admin.activate ? (
                            <span className="ru-badge ru-badge--active">
                              {tr(t, "restaurantUsersActive", "Active")}
                            </span>
                          ) : (
                            <span className="ru-badge ru-badge--inactive">
                              {tr(t, "restaurantUsersInactive", "Inactive")}
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="ru-actions-cell justify-content-end">
                            <button
                              type="button"
                              className="ru-action-icon-btn ru-action-icon-btn--view"
                              onClick={() => handleOpenUserModal(admin, false)}
                              title={tr(t, "restaurantUsersViewDetails", "View details")}
                              aria-label={tr(t, "restaurantUsersViewDetails", "View details")}
                            >
                              <i className="fas fa-eye" aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              className="ru-action-icon-btn ru-action-icon-btn--edit"
                              disabled={admin.isOwner}
                              onClick={() => handleOpenUserModal(admin, true)}
                              title={
                                admin.isOwner
                                  ? tr(
                                      t,
                                      "restaurantUsersOwnerPermissionsLocked",
                                      "Primary owner has full access"
                                    )
                                  : tr(t, "restaurantUsersEditPermissions", "Edit permissions")
                              }
                              aria-label={tr(t, "restaurantUsersEditPermissions", "Edit permissions")}
                            >
                              <i className="fas fa-user-cog" aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              className={`ru-action-icon-btn ${
                                admin.activate
                                  ? "ru-action-icon-btn--deactivate"
                                  : "ru-action-icon-btn--activate"
                              }`}
                              disabled={isBusy || admin.isOwner}
                              onClick={() => handleToggleActive(admin)}
                              title={
                                admin.isOwner
                                  ? tr(t, "restaurantUsersOwnerLocked", "Primary owner cannot be deactivated")
                                  : admin.activate
                                  ? tr(t, "restaurantUsersDeactivate", "Deactivate")
                                  : tr(t, "restaurantUsersActivate", "Activate")
                              }
                              aria-label={
                                admin.activate
                                  ? tr(t, "restaurantUsersDeactivate", "Deactivate")
                                  : tr(t, "restaurantUsersActivate", "Activate")
                              }
                            >
                              {isBusy ? (
                                <span
                                  className="spinner-border spinner-border-sm"
                                  role="status"
                                  aria-hidden="true"
                                />
                              ) : admin.activate ? (
                                <i className="fas fa-user-slash" aria-hidden="true" />
                              ) : (
                                <i className="fas fa-user-check" aria-hidden="true" />
                              )}
                            </button>
                            <button
                              type="button"
                              className="ru-action-icon-btn ru-action-icon-btn--archive"
                              disabled={isBusy || admin.isOwner}
                              onClick={() => handleArchive(admin)}
                              title={
                                admin.isOwner
                                  ? tr(t, "restaurantUsersOwnerArchiveLocked", "Primary owner cannot be archived")
                                  : tr(t, "archive", "Archive")
                              }
                              aria-label={tr(t, "archive", "Archive")}
                            >
                              <i className="fas fa-archive" aria-hidden="true" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination Controls */}
          {!loading && filteredAdmins.length > PAGE_SIZE && (
            <div className="ru-pagination">
              <div className="ru-pagination__text">
                {tr(t, "showing", "Showing")}{" "}
                <strong>{(activePage - 1) * PAGE_SIZE + 1}</strong> {tr(t, "to", "to")}{" "}
                <strong>{Math.min(activePage * PAGE_SIZE, filteredAdmins.length)}</strong>{" "}
                {tr(t, "of", "of")} <strong>{filteredAdmins.length}</strong> {tr(t, "entries", "entries")}
              </div>
              <div className="ru-pagination__controls">
                <button
                  type="button"
                  className="ru-page-btn"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={activePage <= 1}
                  aria-label="Previous Page"
                >
                  <i className="fas fa-chevron-left" aria-hidden="true" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pg) => (
                  <button
                    key={pg}
                    type="button"
                    className={`ru-page-btn ${pg === activePage ? "is-active" : ""}`}
                    onClick={() => setCurrentPage(pg)}
                  >
                    {pg}
                  </button>
                ))}
                <button
                  type="button"
                  className="ru-page-btn"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={activePage >= totalPages}
                  aria-label="Next Page"
                >
                  <i className="fas fa-chevron-right" aria-hidden="true" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* User Details Modal (Simplified & Clean) */}
      {selectedUserModal && (() => {
        const userPerms =
          selectedUserModal.permissions ||
          selectedUserModal.modules ||
          selectedUserModal.accessPermissions;

        const grantedCount = selectedUserModal.isOwner
          ? AVAILABLE_MODULES.length
          : !Array.isArray(userPerms)
          ? AVAILABLE_MODULES.length
          : AVAILABLE_MODULES.filter((m) => userPerms.includes(m.id)).length;

        return (
          <Modal
            show={Boolean(selectedUserModal)}
            onHide={() => setSelectedUserModal(null)}
            centered
            className="ru-modal-simple"
          >
            <Modal.Header closeButton className="ru-sm-header">
              <h5 className="ru-sm-title">
                <i className="fas fa-user-circle me-2 text-primary" aria-hidden="true" />
                {tr(t, "restaurantUsersDetailsTitle", "User Details")}
              </h5>
            </Modal.Header>

            <Modal.Body className="ru-sm-body">
              {/* Profile Card */}
              <div className="ru-sm-profile">
                <div className={`ru-sm-avatar ${selectedUserModal.isOwner ? "is-owner" : ""}`}>
                  {getInitials(selectedUserModal.firstName, selectedUserModal.lastName)}
                </div>

                <div className="ru-sm-user-info">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <h4 className="ru-sm-name">
                      {`${selectedUserModal.firstName || ""} ${selectedUserModal.lastName || ""}`.trim() || "—"}
                    </h4>

                    {selectedUserModal.isOwner ? (
                      <span className="ru-sm-badge owner">
                        <i className="fas fa-crown me-1" aria-hidden="true" />
                        {tr(t, "restaurantUsersPrimaryOwner", "Owner")}
                      </span>
                    ) : (
                      <span className="ru-sm-badge admin">
                        <i className="fas fa-shield-alt me-1" aria-hidden="true" />
                        {tr(t, "restaurantUsersAdmin", "Admin")}
                      </span>
                    )}

                    {selectedUserModal.activate ? (
                      <span className="ru-sm-badge active">
                        <i className="fas fa-check me-1" aria-hidden="true" />
                        {tr(t, "restaurantUsersActive", "Active")}
                      </span>
                    ) : (
                      <span className="ru-sm-badge inactive">
                        <i className="fas fa-times me-1" aria-hidden="true" />
                        {tr(t, "restaurantUsersInactive", "Inactive")}
                      </span>
                    )}
                  </div>

                  <div className="ru-sm-contacts">
                    {selectedUserModal.email && (
                      <span className="ru-sm-contact-item">
                        <i className="fas fa-envelope text-muted" aria-hidden="true" />
                        {selectedUserModal.email}
                      </span>
                    )}
                    {selectedUserModal.phone && (
                      <span className="ru-sm-contact-item ms-2">
                        <i className="fas fa-phone-alt text-muted" aria-hidden="true" />
                        {formatPhoneDisplay(selectedUserModal.phone)}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <hr className="ru-sm-divider" />

              {/* Module Permissions Section Header */}
              <div className="d-flex align-items-center justify-content-between mb-3">
                <span className="ru-sm-section-label">
                  <i className="fas fa-key me-1 text-primary" aria-hidden="true" />
                  {isEditingPerms
                    ? tr(t, "moduleAccessTitle", "Module Access & Permissions")
                    : tr(t, "restaurantUsersAccessTitle", "Module Permissions")}
                </span>
                {isEditingPerms ? (
                  <div className="d-flex align-items-center gap-2">
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-decoration-none fw-bold"
                      onClick={selectAllEditPerms}
                    >
                      {tr(t, "selectAll", "Select All")}
                    </button>
                    <span className="text-muted">|</span>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-decoration-none fw-bold text-danger"
                      onClick={clearAllEditPerms}
                    >
                      {tr(t, "deselectAll", "Clear All")}
                    </button>
                  </div>
                ) : (
                  <span className="ru-sm-count">
                    {grantedCount} / {AVAILABLE_MODULES.length} {tr(t, "authorized", "Authorized")}
                  </span>
                )}
              </div>

              {/* Permission List / Editable Switches */}
              <div className="ru-sm-perm-list">
                {AVAILABLE_MODULES.map((mod) => {
                  if (isEditingPerms) {
                    const isChecked = editPermsSelection.includes(mod.id);
                    return (
                      <div
                        key={mod.id}
                        className={`ru-sm-perm-row ${isChecked ? "bg-white border-primary" : ""}`}
                        style={{ cursor: "pointer" }}
                        onClick={() => toggleEditPerm(mod.id)}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <div className={`ru-sm-mod-icon ${isChecked ? "active" : ""}`}>
                            <i className={mod.icon} aria-hidden="true" />
                          </div>
                          <div>
                            <span className="ru-sm-mod-name d-block">
                              {tr(t, mod.nameKey, mod.defaultName)}
                            </span>
                            <small className="text-muted">
                              {tr(t, mod.descKey, mod.defaultDesc)}
                            </small>
                          </div>
                        </div>

                        <div className="form-check form-switch m-0 ms-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {}}
                            style={{ cursor: "pointer" }}
                          />
                        </div>
                      </div>
                    );
                  }

                  const hasAccess =
                    selectedUserModal.isOwner ||
                    (!Array.isArray(userPerms)
                      ? true
                      : userPerms.includes(mod.id));

                  return (
                    <div key={mod.id} className="ru-sm-perm-row">
                      <div className="d-flex align-items-center gap-2">
                        <div className={`ru-sm-mod-icon ${hasAccess ? "active" : ""}`}>
                          <i className={mod.icon} aria-hidden="true" />
                        </div>
                        <span className="ru-sm-mod-name">
                          {tr(t, mod.nameKey, mod.defaultName)}
                        </span>
                      </div>

                      {hasAccess ? (
                        <span className="ru-sm-status granted">
                          <i className="fas fa-check me-1" aria-hidden="true" />
                          {tr(t, "accessGranted", "Granted")}
                        </span>
                      ) : (
                        <span className="ru-sm-status restricted">
                          <i className="fas fa-lock me-1" aria-hidden="true" />
                          {tr(t, "accessRestricted", "Restricted")}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </Modal.Body>

            <Modal.Footer className="ru-sm-footer d-flex justify-content-end gap-2">
              {isEditingPerms ? (
                <>
                  <button
                    type="button"
                    className="btn btn-light btn-sm px-4 fw-bold"
                    onClick={() => setIsEditingPerms(false)}
                    disabled={savingPerms}
                  >
                    {tr(t, "cancel", "Cancel")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm px-4 fw-bold"
                    onClick={handleSavePermissions}
                    disabled={savingPerms}
                  >
                    {savingPerms ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          role="status"
                          aria-hidden="true"
                        />
                        {tr(t, "saving", "Saving...")}
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check me-1" aria-hidden="true" />
                        {tr(t, "save", "Save Permissions")}
                      </>
                    )}
                  </button>
                </>
              ) : (
                <>
                  {!selectedUserModal.isOwner && (
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm px-3 fw-bold me-auto"
                      onClick={() => {
                        setIsEditingPerms(true);
                        const perms =
                          selectedUserModal.permissions ||
                          selectedUserModal.modules ||
                          selectedUserModal.accessPermissions ||
                          AVAILABLE_MODULES.map((m) => m.id);
                        setEditPermsSelection(
                          Array.isArray(perms)
                            ? [...perms]
                            : AVAILABLE_MODULES.map((m) => m.id)
                        );
                      }}
                    >
                      <i className="fas fa-user-cog me-1" aria-hidden="true" />
                      {tr(t, "restaurantUsersEditPermissions", "Edit Permissions")}
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-light btn-sm px-4 fw-bold"
                    onClick={() => setSelectedUserModal(null)}
                  >
                    {tr(t, "close", "Close")}
                  </button>
                </>
              )}
            </Modal.Footer>
          </Modal>
        );
      })()}
    </>
  );
};

export default RestaurantUsers;
